import { jsx as _jsx } from "react/jsx-runtime";
/* eslint-disable max-len */
import { useEffect, useRef, } from 'react';
import { observer } from 'mobx-react-lite';
import useConversation from './useConversation';
import Conversation from './Conversation';
// for testing
const initialMessages = undefined;
const ConnectedConversation = ({ limitToPageWidth, enableVoiceInput, showItemReferencesWithMessage = true, showItemReferencesCarousel = false, showResetWithInput = true, courseCardShowImage, courseCardShowTitle, courseCardShowInstructor, courseCardShowDescLines, animatedProcessingImage, openWebPagesInNewTab, style, questionStyle, responseStyle, feedbackButton, questionAlignment, responseAlignment, initialMessage, referenceFormatting, inputStyle, startingMessageFontStyle, adminOptions, sendImage, resetConversationLabel, topSpaceReserved = '0px', agentAvatarImage, userAvatarImage, hideRightRailOnMobile, maxItemsInRightRail, enableMultiRefinements, multiRefinementSubmitButtonText, productFormatting, agentName, onProductClick, onPortalRelativeNavigate, itemReferenceDisplay, showPersistentPrompts = true, disableConversationPersistence, messagesContainerRef, streamingMessagesScrollRef, conversationContainerRef, hideFirstMessage = false, disableInternalScroll, disableAutoScroll = false, renderCollapsed, noScroll, onIsLoadingChange, disableArrowScroll, disableAddSpaceAnimation, layoutMode, collapsedMaxHeight, maxPageWidth, onInputHeightChange, messageStackMode, conversationType, }) => {
    const resolvedMessageStackMode = messageStackMode
        ?? (layoutMode === 'dual' ? 'natural' : 'fill');
    const conversationConnect = useConversation({
        agentName,
        layoutMode: layoutMode || 'ai',
        conversationType,
        disableConversationPersistence,
    }, adminOptions, initialMessages);
    // Handle initial message - use ref to avoid infinite loop
    const hasSentInitialMessage = useRef(false);
    // Track if we've already processed reset for this initialMessage
    const hasProcessedResetRef = useRef(false);
    // Track the last processed initialMessage to detect changes
    const lastProcessedInitialMessageRef = useRef(null);
    // Reset the hasSentInitialMessage flag when initialMessage changes (new search)
    useEffect(() => {
        if (initialMessage !== lastProcessedInitialMessageRef.current) {
            // New search query detected - reset the flag to allow processing
            hasSentInitialMessage.current = false;
            hasProcessedResetRef.current = false;
        }
    }, [initialMessage]);
    useEffect(() => {
        // Only process if we haven't sent the initial message yet
        if (hasSentInitialMessage.current) {
            return;
        }
        // Don't proceed if we don't have initialMessage or append function
        if (!initialMessage || !conversationConnect.append) {
            return;
        }
        // Wait for conversation to be ready (have an id)
        if (!conversationConnect.conversation || !conversationConnect.conversation.id) {
            return;
        }
        try {
            // Dual Search: Check if we need to reset for a new search query
            // Reset if initialMessage is different from the first message in the conversation
            const shouldReset = conversationConnect.shouldResetForNewSearch(initialMessage);
            if (conversationConnect.isDualSearchMode
                && !hasProcessedResetRef.current
                && shouldReset) {
                // Mark that we've processed the reset check
                hasProcessedResetRef.current = true;
                // Reset the conversation before processing the new search
                conversationConnect.reset();
                // The reset will trigger a re-render with a new conversation
                return;
            }
            const normalizedQuery = initialMessage.trim().toLowerCase();
            // Check if this exact search query already exists in conversation history
            const messages = conversationConnect.conversation.messages || [];
            const searchAlreadyExists = messages.some((msg) => msg?.role === 'user'
                && msg?.content?.trim().toLowerCase() === normalizedQuery);
            if (searchAlreadyExists) {
                hasSentInitialMessage.current = true;
                lastProcessedInitialMessageRef.current = initialMessage;
                return;
            }
            hasSentInitialMessage.current = true;
            lastProcessedInitialMessageRef.current = initialMessage;
            // Conversation is ready, send the message
            conversationConnect.append({
                role: 'user',
                content: initialMessage,
            });
        }
        catch (error) {
            console.error('[ConnectedConversation] Error in autosearch logic:', error);
            // Fallback: just send the message without duplicate checking
            hasSentInitialMessage.current = true;
            lastProcessedInitialMessageRef.current = initialMessage;
            // Try to send the message even if there was an error
            if (conversationConnect.conversation && conversationConnect.conversation.id) {
                conversationConnect.append({
                    role: 'user',
                    content: initialMessage,
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        initialMessage,
        conversationConnect.conversation,
        conversationConnect.append,
        conversationConnect.isDualSearchMode,
        conversationConnect.shouldResetForNewSearch,
        conversationConnect.reset,
        disableConversationPersistence,
    ]);
    // Notify parent when isLoading changes (for observer control)
    useEffect(() => {
        if (onIsLoadingChange) {
            onIsLoadingChange(conversationConnect.isLoading || false);
        }
    }, [conversationConnect.isLoading, onIsLoadingChange]);
    if (!conversationConnect.conversation) {
        return null;
    }
    return (_jsx(Conversation, { limitToPageWidth: limitToPageWidth, enableVoiceInput: enableVoiceInput, showItemReferencesWithMessage: showItemReferencesWithMessage, showItemReferencesCarousel: showItemReferencesCarousel, courseCardShowImage: courseCardShowImage, courseCardShowTitle: courseCardShowTitle, courseCardShowInstructor: courseCardShowInstructor, courseCardShowDescLines: courseCardShowDescLines, openWebPagesInNewTab: openWebPagesInNewTab, style: style, questionStyle: questionStyle, responseStyle: responseStyle, feedbackButton: feedbackButton, questionAlignment: questionAlignment, responseAlignment: responseAlignment, referenceFormatting: referenceFormatting, inputStyle: inputStyle, startingMessageFontStyle: startingMessageFontStyle, adminOptions: adminOptions, topSpaceReserved: topSpaceReserved, sendImage: sendImage, agentAvatarImage: agentAvatarImage, userAvatarImage: userAvatarImage, resetConversationLabel: resetConversationLabel, showResetWithInput: showResetWithInput, conversationConnect: conversationConnect, hideRightRailOnMobile: hideRightRailOnMobile, enableMultiRefinements: enableMultiRefinements, multiRefinementSubmitButtonText: multiRefinementSubmitButtonText, animatedProcessingImage: animatedProcessingImage, maxItemsInRightRail: maxItemsInRightRail, productFormatting: productFormatting, onProductClick: onProductClick, onPortalRelativeNavigate: onPortalRelativeNavigate, itemReferenceDisplay: itemReferenceDisplay, showPersistentPrompts: showPersistentPrompts, messagesContainerRef: messagesContainerRef, streamingMessagesScrollRef: streamingMessagesScrollRef, conversationContainerRef: conversationContainerRef, hideFirstMessage: hideFirstMessage, disableInternalScroll: disableInternalScroll, disableAutoScroll: disableAutoScroll, renderCollapsed: renderCollapsed, noScroll: noScroll, disableArrowScroll: disableArrowScroll, disableAddSpaceAnimation: disableAddSpaceAnimation, collapsedMaxHeight: collapsedMaxHeight, maxPageWidth: maxPageWidth, onInputHeightChange: onInputHeightChange, messageStackMode: resolvedMessageStackMode }));
};
ConnectedConversation.defaultProps = {
    ...Conversation.defaultProps,
};
export default observer(ConnectedConversation);
//# sourceMappingURL=ConnectedConversation.js.map